import type { Composition } from "@reagent/sim";
import { breakdown, type ReagentShape } from "../reagents";
import "./reagent-chip.css";

/**
 * The DOM half of a reagent's identity, so the job sheet and the HUD name a
 * reagent with the same silhouette the board draws it with. A player who
 * learns "the mint cross is the product" on the sheet has to see that same
 * cross come out of the mixer, or the shape channel teaches nothing.
 *
 * The glyph is inline SVG rather than an emoji or a font glyph: it has to
 * match the 3D primitives exactly, and it has to take its colour from the
 * reagent rather than from the text around it.
 */

const GLYPH_SIZE = 12;

/**
 * Paths in a 0..12 box, matching the meshes in render/hardware/ReagentGlyph.
 * Exported so a caller that needs a bare glyph next to prose it composes
 * itself — the tile inspector's feed readout, which pairs a glyph with a
 * common name `ReagentChip` doesn't know about — doesn't have to re-derive
 * the shape paths.
 */
export function ReagentGlyph({ shape, color }: { shape: ReagentShape; color: string }) {
  const s = GLYPH_SIZE;
  const c = s / 2;
  return (
    <svg
      className="reagent-chip__glyph"
      width={s}
      height={s}
      viewBox={`0 0 ${s} ${s}`}
      aria-hidden
      focusable="false"
    >
      {shape === "sphere" && <circle cx={c} cy={c} r={c - 1} fill={color} />}
      {shape === "cube" && <rect x={1.5} y={1.5} width={s - 3} height={s - 3} rx={1} fill={color} />}
      {shape === "diamond" && <polygon points={`${c},0.5 ${s - 0.5},${c} ${c},${s - 0.5} 0.5,${c}`} fill={color} />}
      {shape === "rod" && <rect x={1} y={c - 2} width={s - 2} height={4} rx={2} fill={color} />}
      {shape === "ring" && (
        <circle cx={c} cy={c} r={c - 2} fill="none" stroke={color} strokeWidth={2.6} />
      )}
      {shape === "wedge" && <polygon points={`${c},1 ${s - 1},${s - 1.5} 1,${s - 1.5}`} fill={color} />}
      {shape === "cross" && (
        <g fill={color}>
          <rect x={1} y={c - 1.6} width={s - 2} height={3.2} rx={1} />
          <rect x={c - 1.6} y={1} width={3.2} height={s - 2} rx={1} />
        </g>
      )}
    </svg>
  );
}

/**
 * One line of a composition: glyph, units, formula. The formula stays as text
 * — the shape is an extra channel, never a replacement for the name, because
 * a silhouette you have not met yet tells you nothing on its own.
 */
export function ReagentChip({ composition }: { composition: Composition }) {
  const parts = breakdown(composition);
  if (parts.length === 0) return null;

  return (
    <span className="reagent-chip">
      {parts.map(({ look, units }) => (
        <span key={look.species} className="reagent-chip__part">
          <ReagentGlyph shape={look.shape} color={look.color} />
          <span className="reagent-chip__text">
            {units}u {look.label}
          </span>
        </span>
      ))}
    </span>
  );
}
